import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaCheck, FaArrowRight, FaMapMarkedAlt, FaSearchLocation } from 'react-icons/fa';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ServiceCrossNav from '../components/ServiceCrossNav';
import './ServicePage.css';
import './Etudes.css';

const Etudes = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="Études Immobilières au Maroc | ATAA REALTY (terrain, SIG, marché)"
        description="Études immobilières au Maroc : études de marché multi-échelles, faisabilité, potentiel foncier, viabilité financière, benchmarks. Approche terrain et SIG par ATAA REALTY."
        canonical="https://ataarealty.com/services/etudes"
        keywords="études immobilières Maroc, étude de marché immobilier, faisabilité immobilière, potentiel foncier, SIG immobilier, programmation immobilière, benchmark immobilier Maroc"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Études Immobilières',
          provider: { '@type': 'ProfessionalService', name: 'ATAA REALTY', url: 'https://ataarealty.com/' },
          areaServed: { '@type': 'Country', name: 'Maroc' },
          description: "Études immobilières analytiques : études de marché, offre/demande, faisabilité, potentiel foncier, viabilité financière, benchmarks nationaux et internationaux.",
          serviceType: "Études immobilières, étude de marché immobilier, faisabilité",
          url: 'https://ataarealty.com/services/etudes'
        }}
      />
      <Navbar />

      {/* HERO */}
      <header className="svc-header svc-bg-etudes">
        <div className="svc-overlay"></div>
        <div className="container svc-title-box svc-anim-up">
          <span className="svc-eyebrow">Analyse de Marché</span>
          <h1>Études Immobilières</h1>
        </div>
      </header>

      <section className="svc-content">
        <div className="container">
          {/* INTRO */}
          <div className="svc-section svc-anim-up svc-delay-1">
            <p className="svc-lead">
              Les études immobilières s'inscrivent dans une logique de
              structuration de référentiels et de modélisation des projets, en
              vue de transformer des données de marché, des cadres
              réglementaires et des hypothèses de développement en supports
              d'aide à la décision.
            </p>

            <p>
              Elles donnent lieu à des livrables analytiques — bases de données,
              matrices de comparaison, modèles économiques, scénarios de
              programmation et notes de synthèse — conçus pour être mobilisés
              par des investisseurs, décideurs, porteurs de projets et instances
              institutionnelles tout au long des phases de conception, de
              validation et de mise en œuvre.
            </p>
          </div>

          {/* DEMARCHE — Two-feature highlight */}
          <div className="svc-section svc-anim-up svc-delay-2">
            <h2 className="svc-section-title">Démarche ATAA REALTY</h2>

            <div className="etudes-features">
              <div className="etudes-feature-card">
                <div className="etudes-feature-icon">
                  <FaSearchLocation />
                </div>
                <h4>Présence systématique sur le terrain</h4>
                <p>
                  Considérée comme la source la plus fiable de collecte, de
                  recoupement et d'affinement des données. Cette observation
                  directe permet de confronter les informations issues des
                  sources documentaires, réglementaires et de marché aux
                  réalités d'usage, d'exploitation et de contexte local.
                </p>
              </div>

              <div className="etudes-feature-card">
                <div className="etudes-feature-icon">
                  <FaMapMarkedAlt />
                </div>
                <h4>Capacité avancée en cartographie &amp; SIG</h4>
                <p>
                  Croisement de multiples couches de données — foncières,
                  urbanistiques, réglementaires, économiques et territoriales —
                  afin de structurer une lecture spatiale cohérente, traçable et
                  actualisée des actifs et de leur environnement.
                </p>
              </div>
            </div>
          </div>

          {/* CHAMP D'INTERVENTION */}
          <div className="svc-section svc-anim-up svc-delay-2">
            <h2 className="svc-section-title">Champ d'intervention</h2>
            <p className="svc-section-intro">
              Nos missions d'études immobilières couvrent notamment :
            </p>
            <ul className="svc-checklist">
              <li>
                <FaCheck />
                <span>
                  <strong>Études de marchés immobiliers multi-échelles</strong>{' '}
                  (quartier, arrondissement, ville, province, région), intégrant
                  l'analyse des dynamiques territoriales et tendances
                  sectorielles.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Études de l'offre et/ou de la demande</strong> :
                  caractérisation des produits existants et projetés, analyse
                  des comportements d'utilisateurs et d'investisseurs,
                  identification des segments de marché.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Veille juridique, urbanistique, fiscale et de marché</strong>{' '}
                  ciblée : information clients sur les évolutions susceptibles
                  d'impacter leurs actifs, analyse des effets et démarches
                  appropriées.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Études de faisabilité économique et immobilière</strong>,
                  accompagnées de propositions de montages juridiques,
                  opérationnels et fiscaux adaptés.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Analyses de potentiel foncier et de constructibilité</strong>,
                  intégrant règles d'urbanisme, servitudes, contraintes
                  techniques et scénarios d'occupation du sol.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Études de positionnement et de programmation immobilière</strong>{' '}
                  : usages, surfaces, typologies de produits et phasages de
                  développement.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Analyses de viabilité financière et de rentabilité prévisionnelle</strong>{' '}
                  : modèles de flux, de coûts et de recettes adaptés aux
                  contextes d'investissement et de développement.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Études d'impact immobilier et territorial</strong>,
                  relatives aux projets d'aménagement, d'infrastructure ou de
                  renouvellement urbain.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Benchmarks de marchés comparables</strong>, à l'échelle
                  nationale ou internationale, pour la mise en perspective des
                  performances, valeurs et pratiques.
                </span>
              </li>
            </ul>
          </div>

          {/* CONTEXTES D'USAGE */}
          <div className="svc-section svc-anim-up svc-delay-3">
            <h2 className="svc-section-title">Contextes d'usage</h2>
            <p className="svc-section-intro">
              Nos études sont mobilisées notamment dans les cadres suivants :
            </p>
            <ul className="svc-checklist">
              <li>
                <FaCheck />
                <span>
                  Phases amont de développement et de programmation de projets
                  immobiliers.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  Décisions d'investissement, de désinvestissement, de gestion,
                  de cession et de valorisation d'actifs.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  Projets d'aménagement, de requalification ou d'extension
                  urbaine.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  Analyses préalables à des opérations de partenariat, de
                  montage institutionnel ou de développement multi-acteurs.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  Suivi des évolutions réglementaires, fiscales, urbanistiques
                  ou de marché, et mise en perspective de leurs effets sur les
                  actifs et stratégies en cours.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="svc-cta-section">
        <h3>Une étude à initier ?</h3>
        <p>
          Faites appel à nos équipes pour structurer un référentiel d'analyse
          adapté à votre projet. Nous combinons terrain, SIG et modélisation
          économique pour fonder vos décisions.
        </p>
        <Link to="/contact" className="svc-cta-btn">
          Lancer un projet d'étude <FaArrowRight />
        </Link>
      </section>

      <ServiceCrossNav currentSlug="etudes" />

      <Footer />
    </>
  );
};

export default Etudes;
